// [M-ALIGN-001 · SEC-AC] La sesión del panel es la misma cuenta de Fluxi: correo + código (y la
// verificación en dos pasos si la cuenta la tiene), y solo entra quien tiene rol ADMIN. Antes el
// "login" comparaba un usuario y una clave que venían dentro de este mismo JavaScript, y agent-config
// no pedía nada: cualquiera podía leer y reescribir los prompts. Ahora agent-config y el
// conversation-engine validan el token de cada llamada, así que todas salen por `authFetch`.

const API_BASE = process.env.REACT_APP_API_URL_FLUXI;
const USER_API = `${API_BASE}/api/ms/ecommerce-service/user`;
const TOKEN_KEY = "fluxi_admin_session";
const DEVICE_KEY = "fluxi_device_id";

export const SESSION_EXPIRED_EVENT = "fluxi:admin-session-expired";

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getToken = () => storage()?.getItem(TOKEN_KEY) || null;
export const saveToken = (token) => storage()?.setItem(TOKEN_KEY, token);
export const clearToken = () => storage()?.removeItem(TOKEN_KEY);

// Mismo identificador de navegador que usa el panel de Fluxi (`X-Fluxi-Device`): ecommerce avisa
// por correo solo la primera vez que una cuenta entra desde un navegador nuevo.
const deviceId = () => {
  const store = storage();
  const saved = store?.getItem(DEVICE_KEY);
  if (saved) return saved;
  const created = window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  store?.setItem(DEVICE_KEY, created);
  return created;
};

// Unas rutas de ecommerce envuelven la respuesta en `data` (perfil, envío del código) y otras no (sesión).
export const unwrap = (json) => (json && typeof json.data === "object" && json.data !== null ? json.data : json || {});

export const hasAdminRole = (roles) =>
  typeof roles === "string" && roles.split(",").map((role) => role.trim()).includes("ADMIN");

const postUser = async (path, body) => {
  const response = await fetch(`${USER_API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Fluxi-Device": deviceId() },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, body: unwrap(json) };
};

export const requestCode = (email) => postUser("/request-verification-code", { email, language: "es" });

export const verifyCode = (email, code) => postUser("/verify-code", { email, code });

// Segundo paso: el código de la app de autenticación (6 dígitos) o uno de los códigos de respaldo.
export const verifySecondFactor = (challenge, value) =>
  postUser("/2fa/verify", /^\d{6}$/.test(value) ? { challenge, code: value } : { challenge, backup_code: value });

export const fetchProfile = async (token) => {
  const response = await fetch(`${USER_API}/profile`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) return { ok: false, status: response.status };
  const profile = unwrap(await response.json().catch(() => ({})));
  return { ok: true, email: profile.email, isAdmin: hasAdminRole(profile.roles) };
};

// Toda llamada del panel a agent-config y al conversation-engine va con la sesión. Si el servidor la
// rechaza (vencida o cerrada), se borra y el panel vuelve a pedir el correo.
export const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
  return response;
};
